import * as React from 'react';
import { AuctionStatus } from '@/types/LiveAuctionState';
import { loadPageData } from '@/redux/modules/appState';
import { useAmplitudeExperiments } from '@/utils/amplitude/experiments/utils/useAmplitudeExperiments';
import { useAppDispatch } from '@/redux/hooks';
import { useEffect } from 'react';
import { useFeatureFlags } from '@liveauctioneers/hammer-ui-core/featureFlags/hooks';
import { useLocation } from 'react-router-dom';
import AuctionIsDone from './layouts/AuctionIsDone/AuctionIsDone';
import BiddingConsolePageHeaderMeta from './components/BiddingConsolePageHeaderMeta/BiddingConsolePageHeaderMeta';
import loadable from '@loadable/component';
import NetworkDisconnectedOverlay from './components/NetworkDisconnectedOverlay/NetworkDisconnectedOverlay';
import ResponsiveBiddingConsole from './layouts/ResponsiveBiddingConsole/ResponsiveBiddingConsole';
import styled from 'styled-components';
import useAuctionStatusChecker from './hooks/useAuctionStatusChecker';
import useBiddingConsolePageData from './hooks/useBiddingConsolePageData';
import WaitingForAuctionToStart from './layouts/WaitingForAuctionToStart/WaitingForAuctionToStart';

/** Establish connection to real-time auction events, only on the client-side */
const LiveAuctionEventsSubscriber = loadable(
    () => import('@/components/LiveEventSubscribers/LiveAuctionEventsSubscriber'),
    {
        ssr: false,
    }
);

const BiddingConsolePage = () => {
    const { variants } = useAmplitudeExperiments();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const { catalogId, liveCatalogStatus, networkStatus } = useBiddingConsolePageData();
    useAuctionStatusChecker(catalogId);
    const featureFlags = useFeatureFlags();

    useEffect(() => {
        dispatch(
            loadPageData({
                amplitudeExperimentFlags: variants,
                featureFlags,
                location,
            })
        );
    }, [dispatch, location, variants, featureFlags]);

    if (!catalogId) {
        return null;
    }

    const auctionIsDone = liveCatalogStatus === AuctionStatus.Done || liveCatalogStatus === AuctionStatus.Ended;
    const auctionIsRunning = liveCatalogStatus === AuctionStatus.Live || liveCatalogStatus === AuctionStatus.Paused;
    const waitingForAuctionToStart = !auctionIsDone && !auctionIsRunning;
    const showNetworkErrorOverlay = auctionIsRunning && networkStatus === 'disconnected';

    return (
        <PageContainer
            $waitingForAuctionToStart={waitingForAuctionToStart}
            data-testid="biddingConsolePage"
        >
            <BiddingConsolePageHeaderMeta catalogId={catalogId} />
            <LiveAuctionEventsSubscriber
                catalogId={catalogId}
                reportUserPresence
            />
            {auctionIsDone && <AuctionIsDone catalogId={catalogId} />}
            {auctionIsRunning && <ResponsiveBiddingConsole catalogId={catalogId} />}
            {waitingForAuctionToStart && <WaitingForAuctionToStart catalogId={catalogId} />}
            {showNetworkErrorOverlay && <NetworkDisconnectedOverlay />}
        </PageContainer>
    );
};

export default BiddingConsolePage;

const PageContainer = styled.div<{ $waitingForAuctionToStart: boolean }>`
    position: relative;
    height: ${({ $waitingForAuctionToStart }) => ($waitingForAuctionToStart ? '100%' : 'auto')};
`;
